import { CSSProperties, useEffect, useMemo, useRef, useState } from 'react';
import { SizeType, FitType, CaptionsPosition, CaptionStyles } from '@goldcast/player-config/src/types/asset-types';
import { getAllCompositeSegments, getClipBroadcastId, getClipFPS } from '@goldcast/player-config/src/utils/clipUtils';
import { VideoElement } from '@goldcast/player-config/src/types/RemotionPlayerConfigTypes/Element/VideoElement';
import { VideoCropPosition } from '@goldcast/player-config/src/types/RemotionPlayerConfigTypes/Scene';
import { LayoutEnum } from '@goldcast/api/content';
import {
  isLayoutAudiogram,
  isLayoutDefault,
  isLayoutFocus,
  isLayoutPip
} from '@goldcast/player-config/src/utils/layout';
import { layoutConfigMap } from '@goldcast/player-config/src/types/speaker-types';
import { getSpeakerLayoutBorderKey } from '@goldcast/utils';
import { isFallingBetweenRange } from '@goldcast/player-config/src/utils/timeUtils';
import { getActiveSpeakersWithinTimeRange } from '@goldcast/player-config/src/utils/activeSpeaker';
import { buildSpeakerDetailsMap } from '@goldcast/player-config/src/generators/compositeSegment/videoFeedAdapters';
import { getVideoCropPositionMapForUploadsVF } from '@goldcast/player-config/src/generators/compositeSegment/getLayoutChangesAndPlayRanges';
import { getCropStyles, getFitCropStyles } from '@goldcast/player-config/src/utils/cropUtils';
import { getAspectRatio } from '@goldcast/player-config/src/utils/sizeUtils';
import { selectVideoFeeds } from '@goldcast/player-config/src/selectors/contentMetadataStoreSelectors';
import AudiogramLayoutPreview from './AudiogramLayoutPreview';
import { classnames } from '@/libs/utils';
import { useClipsContext } from '@/context/ClipsContext/ClipsContext';
import { useContentMetadataStore } from '@/stores/contentMetaDataStore/contentMetaDataStore';
import { getVideoFeedById } from '@/utils/videoFeeds';
import Icon from '@/components/atoms/Icon';

// Module-level font cache: URL → already-registered family name.
// Prevents duplicate FontFace entries in document.fonts when multiple template
// cards mount with the same caption font URL simultaneously.
const captionFontCache = new Map<string, string>();

/**
 * TemplateLayoutPreview mirrors several generator/player functions to produce a
 * static preview that matches what the Remotion player would render. The coupling
 * points below must be kept in sync if the generator logic changes:
 *
 *  - reorderSpeakersByActiveSpeaker  (getLayoutChangesAndPlayRanges.ts)
 *      → videosForCurrentFrame: screenshare-first sort, PIP+screenshare special-case,
 *        active-speaker unshift (including the splice(-1) bug for excluded speakers)
 *  - getVideoCropPositionMapForUploadsVF  (getLayoutChangesAndPlayRanges.ts)
 *      → cropData: face-rec bounding boxes, FILL forced when shouldProcessFaceRecDataForLayouts
 *  - getCropStyles / getFitCropStyles  (cropUtils.ts)
 *      → tile render: getCropStyles for speakers, getFitCropStyles for screenshares
 *  - finalFitMode = 'FILL' when shouldProcessFaceRecDataForLayouts  (getLayoutChangesAndPlayRanges.ts)
 *      → effectiveFit: hasFaceRecData forces FILL regardless of template fit prop
 *  - Screenshare → FOCUS promotion  (getLayoutChangesAndPlayRanges.ts)
 *      → effectiveLayout: GRID becomes FOCUS when screenshare is on stage and in scene
 *  - getAudiogramCaptionPosition  (libs/caption/styles.ts: getStylesForAudiogramCaptionsContainer)
 *      → CaptionOverlay: per-size positions for audiogram layout
 */

// LANDSCAPE (16:9) is wider than the 4:3 card → width-constrained, height follows aspect ratio
// SQUARE (1:1) and PORTRAIT (9:16) are taller than 4:3 → height-constrained, width follows aspect ratio
const SIZE_CLASSES: Record<SizeType, string> = {
  LANDSCAPE: 'w-full aspect-video',
  SQUARE: 'h-full aspect-square',
  PORTRAIT: 'h-full aspect-[9/16]'
};

function getVideosForLayout(presentVideos: VideoElement[], layout: LayoutEnum): VideoElement[] {
  if (!presentVideos.length) {
    return [];
  }
  if (layout === 'SPEAKER') {
    return [presentVideos[0]];
  }
  if (isLayoutAudiogram(layout)) {
    // Audiogram shows a human speaker — screenshares are never appropriate here regardless of
    // whether they carry speakerDetails. The screenshare-first sort would put them at slot 0.
    const speakers = presentVideos.filter(v => !v.name.includes('screenshare'));
    return speakers.length ? [speakers[0]] : [];
  }
  if (isLayoutPip(layout)) {
    return presentVideos.slice(0, 2);
  }
  // Mirror generator caps: FOCUS = screenshare + 8 speakers = 9 max, GRID = 8 max
  return presentVideos.slice(0, isLayoutFocus(layout) ? 9 : 8);
}

export default function TemplateLayoutPreview({
  size,
  layout,
  fit,
  hideBorders,
  magicLayout,
  thumbnailMap,
  absoluteSourceTime = 0,
  currentTime = 0,
  scale = 1,
  subtitle,
  captionPositions,
  captionStyles,
  fontLocation
}: {
  size: SizeType;
  layout: LayoutEnum;
  fit: FitType;
  hideBorders: boolean;
  magicLayout?: { backgroundColor: string; backgroundImage?: string; textColor?: string };
  thumbnailMap: Record<string, string>;
  absoluteSourceTime?: number;
  currentTime?: number;
  scale?: number;
  subtitle?: boolean;
  captionPositions?: CaptionsPosition;
  captionStyles?: CaptionStyles;
  fontLocation?: string;
}) {
  const { clipData, speakersAnalysis } = useClipsContext();

  const clipBroadcastId = useMemo(() => getClipBroadcastId(clipData) ?? '', [clipData]);
  const videoFeeds = useContentMetadataStore(selectVideoFeeds(clipBroadcastId));

  // Caption font lives in captionStyles.font_location (Remotion captions), not template.font_location
  // (that's the legacy ASS caption font).
  const captionFontUrl = captionStyles?.font_location ?? fontLocation;
  const [captionFontFamily, setCaptionFontFamily] = useState<string | undefined>(() =>
    captionFontUrl ? captionFontCache.get(captionFontUrl) : undefined
  );
  const loadingRef = useRef<string | null>(null);
  useEffect(() => {
    if (!captionFontUrl) {
      setCaptionFontFamily(undefined);
      return;
    }
    // Already registered by a sibling card — use the cached family immediately.
    const cached = captionFontCache.get(captionFontUrl);
    if (cached) {
      setCaptionFontFamily(cached);
      return;
    }
    // Guard against concurrent loads for the same URL (multiple cards mounting simultaneously).
    if (loadingRef.current === captionFontUrl) {
      return;
    }
    loadingRef.current = captionFontUrl;

    const family = `tlp-caption-${captionFontUrl.split('/').pop()?.replace(/\W/g, '') ?? 'font'}`;
    const face = new FontFace(family, `url(${captionFontUrl})`);
    document.fonts.add(face);
    face
      .load()
      .then(() => {
        captionFontCache.set(captionFontUrl, family);
        setCaptionFontFamily(family);
      })
      .catch(() => {})
      .finally(() => {
        loadingRef.current = null;
      });
  }, [captionFontUrl]);

  // FIX #7: single fps computation
  const fps = useMemo(() => getClipFPS(clipData), [clipData]);

  // FIX #1: use player-timeline frames (segment.fromFrame/toFrame) to find the current segment.
  // currentTime is in player seconds; segment.fromFrame/toFrame are in player frames — same coordinate.
  const compositeSegment = useMemo(() => {
    const segments = getAllCompositeSegments(clipData);
    if (!segments.length) {
      return undefined;
    }
    const currentPlayerFrame = currentTime * fps;
    return segments.find(s => currentPlayerFrame >= s.fromFrame && currentPlayerFrame <= s.toFrame) ?? segments[0];
  }, [clipData, currentTime, fps]);

  const backgroundStyle = useMemo<CSSProperties>(() => {
    if (magicLayout?.backgroundImage) {
      return {
        backgroundImage: `url(${magicLayout.backgroundImage})`,
        backgroundSize: 'cover',
        backgroundPosition: 'center'
      };
    }
    return { backgroundColor: magicLayout?.backgroundColor };
  }, [magicLayout]);

  // Segment-relative frame: both layoutChanges and playbackAdjustedScenes use this coordinate
  // (same as Remotion's useCurrentFrame() inside the composite <Sequence>).
  const currentFrame = useMemo(() => {
    if (!compositeSegment) {
      return 0;
    }
    return currentTime * fps - compositeSegment.fromFrame;
  }, [currentTime, fps, compositeSegment]);

  // Screenshare detection must be layout-agnostic: layoutAtFrame.hasScreenshare is set by the
  // generator based on the CURRENT clip layout, so it's false when the clip is in DEFAULT mode.
  // Instead, check the screenshare feed's stageTimeSpans directly — independent of clip layout.
  const effectiveLayout = useMemo<LayoutEnum>(() => {
    if (
      !compositeSegment ||
      layout === 'SPEAKER' ||
      layout === 'PIP' ||
      isLayoutDefault(layout) ||
      isLayoutAudiogram(layout)
    ) {
      return layout;
    }
    const screenshareVideo = compositeSegment.videos.find(v => !v.isOriginalVideo && v.name.includes('screenshare'));
    if (!screenshareVideo) {
      return layout;
    }

    // 1. Is screenshare on stage at this time?
    const feed = getVideoFeedById(videoFeeds, screenshareVideo.id);
    const isScreenshareOnStage = !feed || isFallingBetweenRange(feed.stageTimeSpans, absoluteSourceTime, true);
    if (!isScreenshareOnStage) {
      return layout;
    }

    // 2. If there's an active scene, does it include the screenshare?
    const activeScene = compositeSegment.playbackAdjustedScenes?.find(
      s => currentFrame >= s.fromFrame && currentFrame < s.toFrame
    );
    if (activeScene && !activeScene.details.visible_video_ids.includes(screenshareVideo.id)) {
      return layout;
    }

    // 3. Screenshare is on stage (and scene allows it) → treat GRID as FOCUS
    return 'FOCUS';
  }, [compositeSegment, layout, currentFrame, videoFeeds, absoluteSourceTime]);

  const videos = useMemo<VideoElement[]>(() => {
    if (!compositeSegment) {
      return [];
    }
    if (isLayoutDefault(effectiveLayout)) {
      return compositeSegment.videos.filter(v => v.isOriginalVideo);
    }
    return compositeSegment.videos.filter(v => !v.isOriginalVideo);
  }, [compositeSegment, effectiveLayout]);

  // Filter to speakers visible at the current cursor, then apply template layout count rules.
  // Priority: scene track (visible_video_ids) > raw stageTimeSpans presence.
  const videosForCurrentFrame = useMemo<VideoElement[]>(() => {
    if (!compositeSegment) {
      return videos;
    }

    const activeScene = compositeSegment.playbackAdjustedScenes?.find(
      s => currentFrame >= s.fromFrame && currentFrame < s.toFrame
    );
    const sceneVisibleIds = activeScene ? new Set(activeScene.details.visible_video_ids) : null;

    const presentVideos = videos.filter(v => {
      const feed = getVideoFeedById(videoFeeds, v.id);
      // FIX #1 (stageTimeSpans): absoluteSourceTime from TemplatesList now includes trim offset
      // so it correctly matches stageTimeSpans (which are in absolute recording seconds).
      const isOnStage = !feed || isFallingBetweenRange(feed.stageTimeSpans, absoluteSourceTime, true);
      if (sceneVisibleIds) {
        return sceneVisibleIds.has(v.id) && isOnStage;
      }
      return isOnStage;
    });

    // Ordering is driven by the TEMPLATE layout, never by the current clip layout.
    // layoutAtFrame.videoIds encodes the CURRENT clip layout's ordering — using it for a
    // different template layout causes cross-contamination (PIP order bleeding into GRID, etc.).
    //
    // Generator rule for reorderSpeakersByActiveSpeaker:
    //   SPEAKER/PIP/AUDIOGRAM → always active-speaker ordering
    //   FOCUS without screenshare → active-speaker ordering (generator reorders when !isFirstScreenshare)
    //   FOCUS with screenshare → videoFeeds API order (generator skips reorder when isFirstScreenshare)
    //   GRID → videoFeeds API order (no reordering)
    // Screenshare always first (FOCUS with screenshare: generator puts screenshare at slot 0)
    const screenshareIdx = presentVideos.findIndex(v => v.name.includes('screenshare'));
    if (screenshareIdx > 0) {
      const [ss] = presentVideos.splice(screenshareIdx, 1);
      presentVideos.unshift(ss);
    }

    // Active-speaker reorder — mirrors reorderSpeakersByActiveSpeaker exactly.
    // Generator non-PIP branch has no -1 guard: splice(-1, 1) puts the last speaker first
    // when the active speaker is excluded from the scene. We replicate this to match the player.
    const hasScreenshareInPresent = presentVideos.some(v => v.name.includes('screenshare'));

    if (presentVideos.length > 1) {
      const activeSpeakers = getActiveSpeakersWithinTimeRange(
        absoluteSourceTime,
        absoluteSourceTime + 0.001,
        buildSpeakerDetailsMap(videoFeeds)
      );

      if (isLayoutPip(effectiveLayout) && hasScreenshareInPresent) {
        // PIP + screenshare: generator overrides reorderSpeakersByActiveSpeaker with its own path —
        // always [screenshare, activeSpeaker]. Screenshare is already at [0]; put the most active
        // non-screenshare speaker at [1] without disturbing the screenshare slot.
        if (activeSpeakers.length > 0) {
          const mostActiveSpeakerKey = activeSpeakers[0].speakerId;
          const activeIdx = presentVideos.findIndex(
            (v, i) => i > 0 && getVideoFeedById(videoFeeds, v.id)?.speakerKey === mostActiveSpeakerKey
          );
          if (activeIdx > 1) {
            const [speaker] = presentVideos.splice(activeIdx, 1);
            presentVideos.splice(1, 0, speaker);
          }
        }
      } else {
        const needsActiveSpeakerOrdering =
          effectiveLayout === 'SPEAKER' ||
          isLayoutPip(effectiveLayout) ||
          isLayoutAudiogram(effectiveLayout) ||
          (isLayoutFocus(effectiveLayout) && !hasScreenshareInPresent);

        if (needsActiveSpeakerOrdering && activeSpeakers.length > 0) {
          const mostActiveSpeakerKey = activeSpeakers[0].speakerId;
          const activeIdx = presentVideos.findIndex(
            v => getVideoFeedById(videoFeeds, v.id)?.speakerKey === mostActiveSpeakerKey
          );
          // Replicate generator: splice(indexOf || -1) then unshift.
          // When not found (-1), splice(-1,1) takes the last element — matching player behavior.
          const targetIdx = activeIdx !== -1 ? activeIdx : presentVideos.length - 1;
          if (targetIdx > 0) {
            const [speaker] = presentVideos.splice(targetIdx, 1);
            presentVideos.unshift(speaker);
          }
        }
      }
    }

    const fallback = sceneVisibleIds ? [] : videos;

    if (isLayoutDefault(effectiveLayout)) {
      return presentVideos.length ? presentVideos : fallback;
    }
    return getVideosForLayout(presentVideos.length ? presentVideos : fallback, effectiveLayout);
  }, [compositeSegment, effectiveLayout, videos, videoFeeds, absoluteSourceTime, currentFrame]);

  // Crop data split into two maps so the render loop can apply the correct function per video:
  //   faceRecMap  → getCropStyles(isManualRepositioned=false) for speakers,
  //                 getFitCropStyles for screenshares (shows full content without clipping)
  //   manualMap   → getCropStyles(isManualRepositioned=true) — manual scene crops or
  //                 no-face-rec fallback (uploads); uses crop coords directly, no padding expansion
  //
  // Root cause of the original bug: the generator calls getVideoCropPositionMapForUploadsVF with
  // finalVideoIds = only the videos in the CURRENT clip layout. layoutChange.videoCropPositionMap
  // therefore only has entries for those videos. When previewing a GRID template while the clip
  // is in PIP, only 2 of the 8 speakers had crops. Fix: call the function ourselves with ALL
  // template-layout video IDs and the face-rec data at the exact current source time.
  // Generator forces finalFitMode = 'FILL' for all speaker layouts when face-rec data is present
  // (isSpeakerLayout && shouldProcessFaceRecDataForLayouts → finalFitMode = 'FILL').
  // We must use the same fit for our layoutConfigMap lookups, not the template's declared fit.
  const hasFaceRecData = !!(speakersAnalysis?.times?.length && speakersAnalysis?.time_analysis?.length);
  const effectiveFit = hasFaceRecData ? 'FILL' : fit;

  const cropData = useMemo<{
    faceRecMap?: Record<string, VideoCropPosition>;
    faceBoundingBoxMap?: Record<string, VideoCropPosition>;
    manualMap?: Record<string, VideoCropPosition>;
  }>(() => {
    const currentLayoutChange = compositeSegment?.layoutChanges.find(
      lc => currentFrame >= lc.fromFrame && currentFrame <= lc.toFrame
    );
    const manualMap = currentLayoutChange?.videoCropPositionMap;

    if (hasFaceRecData && videosForCurrentFrame.length) {
      const timeIndex = Math.max(
        speakersAnalysis!.times.findLastIndex((t: number) => t <= absoluteSourceTime),
        0
      );
      const facePositions = speakersAnalysis!.time_analysis[timeIndex]?.face_positions;
      if (facePositions?.length) {
        // Use FILL configs — same as the generator's forced finalFitMode when face-rec is active.
        const configLayout =
          isLayoutDefault(effectiveLayout) || effectiveLayout === 'SPEAKER' ? 'GRID' : effectiveLayout;
        const containerConfigs =
          layoutConfigMap[size]?.[configLayout]?.['FILL']?.[getSpeakerLayoutBorderKey(!hideBorders)] ?? {};
        const result = getVideoCropPositionMapForUploadsVF({
          facePositions,
          speakerIdToFaceMap: speakersAnalysis!.speaker_mapping,
          selectedVisibleVideoIds: videosForCurrentFrame.map(v => v.id),
          containerConfigsForAllNumberOfVideos: containerConfigs,
          videoFeeds
        });
        if (result?.videoCropPositionMap) {
          return { faceRecMap: result.videoCropPositionMap, faceBoundingBoxMap: result.faceBoundingBoxMap, manualMap };
        }
      }
    }

    // No face-rec data: layout-change crops are manual scene crops (isManualRepositioned=true).
    return { manualMap };
  }, [
    hasFaceRecData,
    speakersAnalysis,
    absoluteSourceTime,
    videosForCurrentFrame,
    videoFeeds,
    compositeSegment,
    currentFrame,
    size,
    effectiveLayout,
    hideBorders
  ]);

  // FIX #5: depend on the full array so reorders with same count trigger recompute.
  const finalConfig = useMemo(() => {
    const count = videosForCurrentFrame.length;
    if (!count) {
      return [];
    }
    // Mirrors generator rule: DEFAULT, SPEAKER, or any single-video layout → GRID/1
    const useGridOverride = isLayoutDefault(effectiveLayout) || effectiveLayout === 'SPEAKER' || count === 1;
    const configLayout = useGridOverride ? 'GRID' : effectiveLayout;
    const configCount = useGridOverride ? 1 : count;
    // Use effectiveFit (FILL when face-rec active) to match the generator's forced finalFitMode.
    return (
      layoutConfigMap[size]?.[configLayout]?.[effectiveFit]?.[getSpeakerLayoutBorderKey(!hideBorders)]?.[configCount] ??
      []
    );
  }, [videosForCurrentFrame, size, effectiveLayout, effectiveFit, hideBorders]);

  return (
    <div
      data-testid="template-layout-preview"
      className={classnames('relative overflow-hidden', SIZE_CLASSES[size])}
      style={{ ...backgroundStyle, fontSize: `${4.5 * scale}px` }}
    >
      {isLayoutAudiogram(effectiveLayout)
        ? // Audiogram: VideoElement.speakerDetails is only set when clip layout is AUDIOGRAM.
          // Fall back to VideoFeed.speakerDetails (always populated from API) for other clip layouts.
          (() => {
            const video = videosForCurrentFrame[0] ?? null;
            const feedSpeakerDetails = video ? getVideoFeedById(videoFeeds, video.id)?.speakerDetails : undefined;
            return (
              <AudiogramLayoutPreview
                size={size}
                video={video}
                speakerDetailsFallback={feedSpeakerDetails}
                textColor={magicLayout?.textColor}
              />
            );
          })()
        : finalConfig.map((config, i) => {
            const video = videosForCurrentFrame[i];
            const src = video?.details.preview ? thumbnailMap[video.details.preview] : undefined;
            const mergedContainerStyle = {
              ...(video.containerStyle as CSSProperties),
              ...(config.containerStyle as CSSProperties),
              borderRadius: '0.2em',
              // When clip layout is AUDIOGRAM, VideoElements have visibility:hidden set on containerStyle
              // to hide them in the player. Always show them in the template preview.
              visibility: 'visible' as const,
              // Crop transforms scale the image beyond the container; clip it.
              overflow: 'hidden' as const
            };
            // Pick the crop and function to match the generator's rules:
            //   screenshare + face-rec → getFitCropStyles (shows full content without edge clipping)
            //   speaker + face-rec     → getCropStyles(isManualRepositioned=false, applies person-boundary padding)
            //   manual scene crop      → getCropStyles(isManualRepositioned=true, uses coords directly)
            //   manual overrides face-rec for same video (matching generator's merge order)
            const isScreenshare = video.name.includes('screenshare');
            const faceRecCrop = cropData.faceRecMap?.[video.id] ?? null;
            const manualCrop = cropData.manualMap?.[video.id] ?? null;
            const effectiveCrop = manualCrop ?? faceRecCrop;
            let cropStyles: CSSProperties = {};
            // faceCoordinates determines whether getCropStyles uses face-padding (50% extra, for face-only BB)
            // or person-padding (20% top, for person BB). Without it we'd always use person-padding even
            // when only a face bounding box is available, causing a tighter framing than the player.
            const faceCoordinates = cropData.faceBoundingBoxMap?.[video.id] ?? undefined;
            // Mirrors the generator's allOtherCropInformation: face bboxes for other videos, overridden
            // by person/face bboxes for the same keys. This prevents the crop from extending into another
            // speaker's region in the shared source frame, producing the same zoom level as the player.
            // Only applied for face-rec crops (same as generator's preventOverlappingWithOtherCrops flag).
            const allOtherCropInformation: Record<string, VideoCropPosition> | undefined =
              hasFaceRecData && cropData.faceRecMap
                ? {
                    ...Object.fromEntries(
                      Object.entries(cropData.faceBoundingBoxMap ?? {}).filter(([id]) => id !== video.id)
                    ),
                    ...Object.fromEntries(Object.entries(cropData.faceRecMap).filter(([id]) => id !== video.id))
                  }
                : undefined;
            if (isScreenshare && faceRecCrop && !manualCrop) {
              cropStyles = getFitCropStyles(
                faceRecCrop,
                config.containerStyle as CSSProperties,
                getAspectRatio(size),
                video.aspectRatio
              );
            } else if (effectiveCrop) {
              cropStyles = getCropStyles(
                effectiveCrop,
                config.containerStyle as CSSProperties,
                getAspectRatio(size),
                allOtherCropInformation,
                manualCrop !== null,
                video.aspectRatio,
                faceCoordinates
              );
            }
            const mergedStyle = {
              ...(video.style as CSSProperties),
              ...(config.style as CSSProperties),
              ...(isScreenshare ? { objectFit: 'contain' as const } : {}),
              ...cropStyles
            };
            return (
              <div key={video.id} style={mergedContainerStyle}>
                {src ? (
                  <img src={src} alt="" style={mergedStyle} />
                ) : (
                  <div
                    data-testid="thumbnail-skeleton"
                    className={`absolute inset-0 flex animate-pulse items-center justify-center ${getVideoFeedById(videoFeeds, video.id)?.speakerDetails?.lightBgColor ?? 'bg-slate-200'}`}
                  >
                    <Icon name="IconPhoto" className="size-1/4 opacity-40" />
                  </div>
                )}
              </div>
            );
          })}
      {(subtitle || isLayoutAudiogram(effectiveLayout)) && (captionStyles || captionPositions) && (
        <CaptionOverlay
          captionPositions={captionPositions}
          captionStyles={captionStyles}
          fontFamily={captionFontFamily}
          layout={effectiveLayout}
          size={size}
        />
      )}
    </div>
  );
}

// caption_positions values can be percentage numbers (0–100) or percentage strings ("2.5%"),
// matching the same coordinate space used by the Remotion caption renderer.
// For audiogram, the player ignores caption_positions entirely and uses fixed per-size positions
// (mirroring getStylesForAudiogramCaptionsContainer in libs/caption/styles.ts).
function getAudiogramCaptionPosition(size: SizeType): CSSProperties {
  if (size === 'LANDSCAPE') {
    return {
      left: '52%',
      top: '4%',
      width: '46%',
      height: '40%',
      justifyContent: 'flex-start',
      alignItems: 'flex-start'
    };
  }
  if (size === 'SQUARE') {
    return {
      left: '2%',
      top: '87%',
      width: '81%',
      height: '11%',
      justifyContent: 'flex-start',
      alignItems: 'flex-start'
    };
  }
  // PORTRAIT
  return {
    left: '4%',
    top: '72%',
    width: '92%',
    height: '16%',
    justifyContent: 'flex-start',
    alignItems: 'flex-start'
  };
}

function CaptionOverlay({
  captionPositions,
  captionStyles,
  fontFamily,
  layout,
  size
}: {
  captionPositions?: CaptionsPosition;
  captionStyles?: CaptionStyles;
  fontFamily?: string;
  layout: LayoutEnum;
  size: SizeType;
}) {
  const toPercent = (v: number | string | undefined, fallback: string) =>
    v === undefined ? fallback : typeof v === 'string' ? v : `${v}%`;

  const positionOverride = isLayoutAudiogram(layout) ? getAudiogramCaptionPosition(size) : undefined;

  const containerStyle: CSSProperties = {
    position: 'absolute',
    left: positionOverride?.left ?? toPercent(captionPositions?.x, '2.5%'),
    top: positionOverride?.top ?? toPercent(captionPositions?.y, '82%'),
    width: positionOverride?.width ?? toPercent(captionPositions?.width, '95%'),
    height: positionOverride?.height ?? toPercent(captionPositions?.height, '18%'),
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: positionOverride?.alignItems ?? 'center',
    justifyContent: positionOverride?.justifyContent ?? 'center',
    gap: '0.25em',
    overflow: 'hidden',
    fontFamily,
    // Extract only color/background from pageStyle — stroke and paint-order break at small scales.
    color: captionStyles?.pageStyle?.color as string | undefined,
    background: captionStyles?.pageStyle?.background as string | undefined
  };

  const words: { text: string; active: boolean }[] = [
    { text: 'Sample', active: false },
    { text: 'caption', active: true },
    { text: 'text', active: false }
  ];

  // Mirror the player exactly: highlightBoxStyle is only applied for highlightType === 'box',
  // as a separate absolute div behind the text (page.highlightBoxStyle in CaptionsPageInternal).
  // The template uses `background` (shorthand), not `backgroundColor`.
  const isBoxHighlight = captionStyles?.wordStyle?.highlightType === 'box';
  const hbs = captionStyles?.wordStyle?.highlightBoxStyle as (CSSProperties & { background?: string }) | undefined;
  const highlightBg = hbs?.background ?? (hbs?.backgroundColor as string | undefined);

  return (
    <div style={containerStyle}>
      {words.map(({ text, active }, i) => {
        const wordStyle: CSSProperties = {
          fontSize: '1em',
          lineHeight: 1,
          padding: '0.1em 0.25em',
          borderRadius: '0.2em',
          ...stripRenderingProps(active ? captionStyles?.wordStyle?.active : captionStyles?.wordStyle?.inactive)
        };

        if (active && isBoxHighlight && highlightBg) {
          // Absolute box behind text — matches the player's captions-page-highlight-box pattern.
          // inset uses em so it scales with font-size; approximates typical -6px/-12px insets.
          return (
            <span key={i} style={{ position: 'relative', display: 'inline-block', ...wordStyle }}>
              <span
                style={{
                  position: 'absolute',
                  inset: '-0.1em -0.2em',
                  background: highlightBg,
                  borderRadius: '0.2em',
                  zIndex: 0
                }}
              />
              <span style={{ position: 'relative', zIndex: 1 }}>{text}</span>
            </span>
          );
        }

        return (
          <span key={i} style={wordStyle}>
            {text}
          </span>
        );
      })}
    </div>
  );
}

function stripRenderingProps(style: CSSProperties | undefined): CSSProperties {
  if (!style) {
    return {};
  }
  const {
    fontSize: _fs,
    WebkitTextStrokeWidth: _sw,
    WebkitTextStrokeColor: _sc,
    paintOrder: _po,
    textStroke: _ts,
    ...rest
  } = style as CSSProperties & {
    WebkitTextStrokeWidth?: unknown;
    WebkitTextStrokeColor?: unknown;
    paintOrder?: unknown;
    textStroke?: unknown;
  };
  return rest;
}
